import { CachedLearningAudio } from '@shared/media/audio/CachedLearningAudio'
import { useRef, useState } from 'react'
import { Pause, Volume2 } from 'lucide-react'
import {
  LmsButton,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@shared/ui'
import type { ButtonSize } from '@shared/ui'
import './inline-audio-player.css'

function resolveAudioButtonSize(size: 'small' | 'medium' | 'large'): ButtonSize {
  if (size === 'small') {
    return 'icon-sm'
  }

  if (size === 'large') {
    return 'icon-lg'
  }

  return 'icon'
}

export function InlineAudioPlayer({
  audio,
  labels,
  size = 'medium',
}: {
  readonly audio?: string | null
  readonly labels?: { readonly play: string; readonly stop: string; readonly unavailable: string }
  readonly size?: 'small' | 'medium' | 'large'
}) {
  const audioElementRef = useRef<HTMLAudioElement | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const playLabel = labels?.play ?? 'Play audio'
  const stopLabel = labels?.stop ?? 'Stop audio'
  const unavailableLabel = labels?.unavailable ?? 'Audio playback is unavailable for this clip in the current browser.'

  if (!audio) {
    return null
  }

  const handleToggle = async () => {
    if (!audioElementRef.current) {
      return
    }

    if (isPlaying) {
      audioElementRef.current.pause()
      audioElementRef.current.currentTime = 0
      setErrorMessage(null)
      setIsPlaying(false)
      return
    }

    try {
      setErrorMessage(null)
      await audioElementRef.current.play()
      setIsPlaying(true)
    } catch {
      setIsPlaying(false)
      setErrorMessage(unavailableLabel)
    }
  }

  return (
    <div
      className="inlineAudioPlayer"
      data-audio-state={errorMessage ? 'error' : isPlaying ? 'playing' : 'idle'}
    >
      <TooltipProvider delayDuration={250}>
        <Tooltip>
          <TooltipTrigger asChild>
            <LmsButton
              aria-label={isPlaying ? stopLabel : playLabel}
              aria-pressed={isPlaying}
              className="inlineAudioPlayerButton"
              onClick={() => void handleToggle()}
              size={resolveAudioButtonSize(size)}
              variant={isPlaying ? 'secondary' : 'outline'}
            >
              {isPlaying ? (
                <Pause aria-hidden="true" className="size-4" />
              ) : (
                <Volume2 aria-hidden="true" className="size-4" />
              )}
            </LmsButton>
          </TooltipTrigger>
          <TooltipContent>{isPlaying ? stopLabel : playLabel}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
      {errorMessage ? (
        <p aria-live="polite" className="inlineAudioPlayerError">
          {unavailableLabel}
        </p>
      ) : null}
      <CachedLearningAudio
        onError={() => {
          setErrorMessage(unavailableLabel)
          setIsPlaying(false)
        }}
        onPause={() => {
          setIsPlaying(false)
        }}
        onEnded={() => {
          setIsPlaying(false)
        }}
        ref={audioElementRef}
        src={audio}
      />
    </div>
  )
}
